import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity, Scissors, Hospital, Calendar, Download, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function brl(n) { return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }
function num(n) { return (Number(n) || 0).toLocaleString("pt-BR"); }

function Bar({ label, value, max, suffix, to, testid }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const inner = (
    <div className={`flex items-center gap-3 p-2 -mx-2 rounded-md transition-colors ${to ? "hover:bg-slate-50 cursor-pointer" : ""}`}>
      <div className="w-44 text-sm font-medium text-slate-700 truncate" title={label}>{label}</div>
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-blue-700 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-28 text-right text-sm font-semibold text-slate-900">
        {suffix === "brl" ? brl(value) : num(value)}
      </div>
    </div>
  );
  return to ? <Link to={to} data-testid={testid}>{inner}</Link> : inner;
}

export default function ProducaoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", surgeon_id: "", hospital_id: "", procedure_id: "" });
  const [surgeons, setSurgeons] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [procedures, setProcedures] = useState([]);

  useEffect(() => {
    api.get("/surgeons").then((r) => setSurgeons(r.data || []));
    api.get("/hospitals").then((r) => setHospitals(r.data || []));
    api.get("/procedures").then((r) => setProcedures(r.data || []));
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/reports/production", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  async function onExport() {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      await downloadFile("/reports/production.xlsx", "producao.xlsx", params);
      toast.success("Relatório de produção gerado.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const maxSurgeon = useMemo(() => Math.max(1, ...((data?.by_surgeon || []).map((x) => x.total))), [data]);

  // Drill-down para /casos carregando o MESMO recorte da busca (período,
  // hospital, cirurgião, procedimento) — não a produção inteira da pessoa.
  function casesLink(extra = {}) {
    const q = new URLSearchParams();
    const merged = { ...filters, ...extra };
    ["date_from", "date_to", "surgeon_id", "hospital_id", "procedure_id"].forEach((k) => { if (merged[k]) q.set(k, merged[k]); });
    const s = q.toString();
    return `/casos${s ? `?${s}` : ""}`;
  }
  const maxProc = useMemo(() => Math.max(1, ...((data?.by_procedure || []).map((x) => x.total))), [data]);
  const maxHosp = useMemo(() => Math.max(1, ...((data?.by_hospital || []).map((x) => x.total))), [data]);
  const maxMonth = useMemo(() => Math.max(1, ...((data?.by_month || []).map((x) => x.total))), [data]);

  const t = data?.totals || {};

  // --- Suspensions tab state ---
  const [susp, setSusp] = useState(null);
  const [suspLoading, setSuspLoading] = useState(false);
  async function loadSusp() {
    setSuspLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await api.get("/reports/suspensions", { params });
      setSusp(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSuspLoading(false); }
  }

  return (
    <div className="space-y-5" data-testid="producao-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Produção</h1>
          <p className="text-sm text-slate-500 mt-1">Visão consolidada por cirurgião, procedimento, hospital e período.</p>
        </div>
        <Button onClick={onExport} className="bg-blue-700 hover:bg-blue-800 h-11 md:h-9 w-full sm:w-auto" data-testid="btn-export-production">
          <Download className="h-4 w-4 mr-2" /> Exportar Excel
        </Button>
      </div>

      <Tabs defaultValue="producao" onValueChange={(v) => { if (v === "suspensoes" && !susp) loadSusp(); }}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="producao" data-testid="tab-producao">Produção</TabsTrigger>
          <TabsTrigger value="suspensoes" data-testid="tab-suspensoes">Suspensões</TabsTrigger>
        </TabsList>

        <TabsContent value="producao" className="space-y-5 pt-3">

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">De</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Até</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-to" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Cirurgião</label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={filters.surgeon_id} onChange={(e) => setFilters({ ...filters, surgeon_id: e.target.value })} data-testid="filter-surgeon">
              <option value="">Todos</option>
              {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Hospital</label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={filters.hospital_id} onChange={(e) => setFilters({ ...filters, hospital_id: e.target.value })} data-testid="filter-hospital">
              <option value="">Todos</option>
              {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Procedimento</label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={filters.procedure_id} onChange={(e) => setFilters({ ...filters, procedure_id: e.target.value })} data-testid="filter-procedure">
              <option value="">Todos</option>
              {procedures.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Link to={casesLink()} className="block" data-testid="link-kpi-total">
          <Card className="border-slate-200 shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Casos</div>
            <div className="text-3xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-total">{num(t.total)}</div>
          </CardContent></Card>
        </Link>
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><Scissors className="h-3.5 w-3.5" /> Procedimentos</div>
          <div className="text-3xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-procedures">{num(t.procedures_performed ?? t.total)}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">inclui múltiplos por caso</div>
        </CardContent></Card>
        <Link to="/faturamento" className="block" data-testid="link-kpi-approved">
          <Card className="border-slate-200 shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Aprovado</div>
            <div className="text-2xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-approved">{brl(t.approved)}</div>
          </CardContent></Card>
        </Link>
        <Link to="/faturamento?status=faturado" className="block" data-testid="link-kpi-billed">
          <Card className="border-slate-200 shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Faturado</div>
            <div className="text-2xl font-display font-bold text-slate-900 mt-1" data-testid="kpi-billed">{brl(t.billed)}</div>
          </CardContent></Card>
        </Link>
        <Link to="/faturamento?status=faturado" className="block" data-testid="link-kpi-received">
          <Card className="border-slate-200 shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Recebido</div>
            <div className="text-2xl font-display font-bold text-emerald-700 mt-1" data-testid="kpi-received">{brl(t.received)}</div>
          </CardContent></Card>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-200 shadow-sm"><CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Scissors className="h-4 w-4 text-blue-700" /> Por cirurgião</h2>
          <div className="mt-5 space-y-3">
            {(data?.by_surgeon || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Sem dados.</div>}
            {(data?.by_surgeon || []).slice(0, 15).map((s, i) => (
              <Bar key={s.id || i} label={s.name} value={s.total} max={maxSurgeon} to={s.id ? casesLink({ surgeon_id: s.id }) : null} testid={`prod-surgeon-${i}`} />
            ))}
          </div>
        </CardContent></Card>

        <Card className="border-slate-200 shadow-sm"><CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Activity className="h-4 w-4 text-blue-700" /> Por procedimento</h2>
          <div className="mt-5 space-y-3">
            {(data?.by_procedure || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Sem dados.</div>}
            {(data?.by_procedure || []).slice(0, 15).map((p, i) => (
              <Bar key={p.id || i} label={p.name} value={p.total} max={maxProc} to={p.id ? casesLink({ procedure_id: p.id }) : null} testid={`prod-procedure-${i}`} />
            ))}
          </div>
        </CardContent></Card>

        <Card className="border-slate-200 shadow-sm"><CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Hospital className="h-4 w-4 text-blue-700" /> Por hospital</h2>
          <div className="mt-5 space-y-3">
            {(data?.by_hospital || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Sem dados.</div>}
            {(data?.by_hospital || []).slice(0, 15).map((h, i) => (
              <Bar key={h.id || i} label={h.name} value={h.total} max={maxHosp} to={h.id ? casesLink({ hospital_id: h.id }) : null} testid={`prod-hospital-${i}`} />
            ))}
          </div>
        </CardContent></Card>

        <Card className="border-slate-200 shadow-sm"><CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Calendar className="h-4 w-4 text-blue-700" /> Produção mensal</h2>
          <div className="mt-5 space-y-3">
            {(data?.by_month || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Sem dados.</div>}
            {(data?.by_month || []).slice(-12).map((m, i) => (
              <Bar key={m.month || i} label={m.month} value={m.total} max={maxMonth} />
            ))}
          </div>
        </CardContent></Card>
      </div>

      <Card className="border-slate-200 shadow-sm"><CardContent className="p-6">
        <h2 className="font-display text-lg font-semibold text-slate-900">Casos por status</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {(data?.by_status || []).map((s) => (
            <span key={s.status} className="px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs text-slate-700" data-testid={`status-pill-${s.status}`}>
              {s.label} · <b>{s.total}</b>
            </span>
          ))}
          {(data?.by_status || []).length === 0 && <span className="text-sm text-slate-500">Sem dados.</span>}
        </div>
      </CardContent></Card>

      {loading && <div className="text-center text-sm text-slate-500 py-2">Carregando…</div>}
        </TabsContent>

        <TabsContent value="suspensoes" className="space-y-5 pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200 shadow-sm"><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Total suspensas</div>
              <div className="text-3xl font-display font-bold text-red-600 mt-1" data-testid="kpi-suspensions-total">{num(susp?.totals?.total || 0)}</div>
            </CardContent></Card>
            {(susp?.by_reason || []).map((r) => (
              <Card key={r.reason} className="border-slate-200 shadow-sm" data-testid={`kpi-reason-${r.reason}`}>
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{r.reason_label}</div>
                  <div className="text-2xl font-display font-bold text-slate-900 mt-1">{num(r.total)}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3">Código</th>
                    <th className="text-left px-4 py-3">Data</th>
                    <th className="text-left px-4 py-3">Período</th>
                    <th className="text-left px-4 py-3">Paciente</th>
                    <th className="text-left px-4 py-3">Cirurgião</th>
                    <th className="text-left px-4 py-3">Procedimento</th>
                    <th className="text-left px-4 py-3">Motivo</th>
                    <th className="text-left px-4 py-3">Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {suspLoading && <tr><td colSpan={8} className="text-center p-6 text-slate-400">Carregando…</td></tr>}
                  {!suspLoading && (susp?.items || []).length === 0 && (
                    <tr><td colSpan={8} className="text-center p-8 text-slate-400">Nenhuma cirurgia suspensa no período.</td></tr>
                  )}
                  {(susp?.items || []).map((it, idx) => (
                    <tr key={it.id} className="border-b border-slate-100" data-testid={`susp-row-${idx}`}>
                      <td className="px-4 py-3 font-mono text-xs">{it.case_code}</td>
                      <td className="px-4 py-3">{it.surgery_date}</td>
                      <td className="px-4 py-3 capitalize">{it.period}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{it.patient_name || "—"}</td>
                      <td className="px-4 py-3">{it.surgeon_name || "—"}</td>
                      <td className="px-4 py-3">{it.procedure_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                          {it.reason_label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{it.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
